<template>
  <div>
    <v-form ref="surveyForm" v-model="formIsValid" v-if="question">
      <v-card>
        <v-card-title id="question-title"><b>{{ resolveQuestionTitle() }}</b></v-card-title>
        <v-card-text v-if="question.isCheckBox()">
          <v-checkbox
              v-model="checkbox"
              v-for="option in question.resolveOptions()"
              v-bind:key="option.key"
              :label="option.label"
              :value="option.key"
              :rules="[rules.required]"
              @change="updateQuestion"
          ></v-checkbox>
        </v-card-text>
        <v-card-text v-if="question.isTextBox()">
          <v-textarea v-model="text" @keyup="updateAnswerOnChange(text)" :rules="[rules.required]"></v-textarea>
        </v-card-text>
        <v-card-text v-if="question.isNumber()">
          <v-text-field v-model="text" type="number" @keyup="updateAnswerOnChange(text)" :rules="[rules.required]" clearable/>
        </v-card-text>
        <v-card-text v-if="question.isPhoto()">
          <camera :question-id="question.id"></camera>
          <gallery :photos="question.resolvePhotoUrls()"></gallery>
        </v-card-text>
        <v-card-text v-if="question.isTelephone()">
          <v-text-field v-model="telephone"
                        type="tel"
                        @keyup="saveAndRender(telephone)"
                        :rules="[rules.required, rules.telephone]"
                        clearable/>
        </v-card-text>
        <v-card-text ref="email" v-if="question.isEmail()">
          <v-text-field v-model="text" type="email" :rules="[rules.required, rules.email]"
                        @keyup="updateAnswerOnChange(text)" clearable/>
        </v-card-text>
        <v-card-text v-if="question.isRadio()">
          <v-radio-group v-model="radio" @change="updateAnswerOnChange(radio)" :rules="[rules.required]">
            <v-radio
                v-for="option in question.resolveOptions()"
                :key="option.key"
                :label="option.label"
                :value="option.key"
            ></v-radio>
          </v-radio-group>
        </v-card-text>
        <v-card-text v-if="question.isSelectBox()">
          <v-select
              v-model="select"
              :items="question.options"
              :rules="[rules.required]"
              item-text="label"
              item-value="key"
              @change="updateQuestion"
          >
          </v-select>
        </v-card-text>
        <v-card-text v-if="question.isSignature()">
          <signature :question-id="question.id" :signature-url="question.answer" :bus="signatureBus"></signature>
        </v-card-text>
        <v-card-actions v-if="question.isEndSurvey()">
          <v-spacer></v-spacer>
          <v-btn color="primary" :disabled="!showButtons" @click="restartSurvey">
            <v-icon>close</v-icon>
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
        <v-card-actions v-else>
          <v-btn color="primary" :disabled="!showButtons" @click="loadPreviousQuestion">
            <v-icon>navigate_before</v-icon>
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn @click="openAddressOverview">overzicht</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" v-if="question.type === 'signature'"
                 @click="updateQuestionSignature">
            <v-icon>navigate_next</v-icon>
          </v-btn>
          <v-btn v-else color="primary" :disabled="!showButtons || !formIsValid" @click="updateAnswerService">
            <v-icon>navigate_next</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </div>
</template>
<style scoped>
#question-title {
  font-size: large;
}
</style>
<script>
import {Question} from "@/shared/question";
import {webSocketService, questionCookieService} from "@/services/question";
import {service} from "@/services/address"
import {mapActions} from "vuex";
import Camera from "@/components/question/photo/Camera.vue";
import Gallery from "@/components/question/photo/Gallery.vue";
import Signature from "@/components/question/photo/Signature.vue";
import Vue from "vue";

export const AREA_CODE_NL = /(045|079|078|077|076|075|074|013|014|015|073|072|071|070|058|055|053|050|046|010|044|043|040|038|036|020|035|033|030|026|024|023)/gi;

export default {
  name: "question-navigator",
  components: {Signature, Camera, Gallery},
  data() {
    return {
      question: Question.initDefault(),
      enableDebugButtons: false,
      formIsValid: true,
      signatureBus: new Vue(),
      debug: false,
      checkbox: [],
      radio: null,
      select: null,
      telephone: null,
      photo: null,
      photos: [],
      text: null,
      showButtons: true,
      showSnackbar: false,
      rules: {
        required: value => {
          if (this.question.required) {
            return !!value || "Verplicht veld";
          }
          return true;
        },
        telephone: value => {
          if (value) {
            return value.replaceAll(" ", "").length === 10 || "Telefoonnummer heeft 10 getallen";
          }
          return false;
        },
        email: value => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          return pattern.test(value) || "E-mail adres incorrect";
        }
      }
    }
  },
  methods: {
    ...mapActions(["loadAddress"]),
    clear() {
      this.checkbox = [];
      this.radio = null;
      this.select = null;
      this.text = null;
      this.telephone = null;
      this.$refs.surveyForm.reset();
    },
    updateAnswerOnChange(key) {
      this.question.answer = key;
      this.$refs.surveyForm.validate();
    },
    updateQuestion() {
      if (this.question.isTextBox() || this.question.isTextField()) {
        this.question.answer = this.text;
      } else if (this.question.isRadio()) {
        this.question.answer = this.radio;
      } else if (this.question.isSelectBox()) {
        this.question.answer = this.select;
      } else if (this.question.isCheckBox()) {
        if (this.checkbox && this.checkbox.length > 0) {
          this.question.answer = this.checkbox.join(",");
        } else {
          this.question.answer = null;
        }
      } else if (this.question.isPhoto()) {
        if (this.photo) {
          this.photos.push(this.photo);
        }
        this.question.answer = this.photos;
      }
    },
    saveAndRender(value) {
      if (value) {
        value = value.replaceAll(" ", "");
        let pattern = [4, 2, 2, 2];   // #### ## ## ##
        if (value.startsWith("06")) {
          pattern = [2, 2, 2, 2, 2];  // ## ## ## ## ##
        } else if (value.match(AREA_CODE_NL)) {
          pattern = [3, 3, 2, 2];     // ### ### ## ##
        }

        const tempValue = this.buildPhoneNumber(value, pattern);
        this.telephone = tempValue.trim();
        this.question.answer = tempValue.replaceAll(" ", "");
      } else {
        this.telephone = "";
        this.question.answer = "";
      }
    },
    updateQuestionSignature() {
      this.signatureBus.$emit('saveSignature');
    },
    buildPhoneNumber(number, pattern) {
      let tempNumber = "";
      let sp = 0;
      for (let i = 0; i < pattern.length; i++) {
        tempNumber = tempNumber.concat(" ", number.substring(sp, sp + pattern[i]));
        sp = sp + pattern[i];
      }
      return tempNumber;
    },
    updateAnswer() {
      if (this.question.answer) {
        if (this.question.isTextBox() || this.question.isTextField()) {
          this.text = this.question.answer;
        } else if (this.question.isTelephone()) {
          this.saveAndRender(this.question.answer);
        } else if (this.question.isRadio()) {
          this.radio = this.question.answer;
        } else if (this.question.isSelectBox()) {
          this.select = this.question.options.filter(o => o.key === this.question.answer)[0];
        } else if (this.question.isCheckBox()) {
          this.checkbox = this.question.answer.split(",");
        } else if (this.question.isPhoto()) {
          this.photos = this.question.answer;
        }
      }
    },
    restartSurvey() {
      this.$router.push("/address");
    },
    resolveQuestionTitle() {
      if (this.question.isEndSurvey()) {
        return this.question.question || "Einde vragenlijst"
      }
      return this.question.question;
    },
    openAddressOverview() {
      this.$router.push("/address");
    },
    loadPreviousQuestion() {
      webSocketService.send({"action": "goToPreviousPage", "questionId": this.question.id});
    },
    updateAnswerService() {
      if (this.question.isPhoto()) {
        webSocketService.send({"action": "goToNextPage", "questionId": this.question.id});
      } else {
        webSocketService.send({"answer": this.question.answer, "questionId": this.question.id});
      }
    }
  }, mounted() {
    webSocketService.bindOnMessage(event => {
      let questionFromEvent = Question.from(event.data);
      this.clear();
      if (questionFromEvent.isEndSurvey()) {
        service.changeSurveyReadiness(questionCookieService.getActiveSurvey())
            .then(resp => {
              this.loadAddress(resp.data);
              this.openAddressOverview();
            })
      } else {
        this.question = questionFromEvent;
        this.updateAnswer();
      }
    });
  }

}

</script>
